import React, { useEffect, useRef, useState } from "react";

import {
  Box,
  Button,
  Flex,
  Text,
  Stack,
  useDisclosure,
  Spinner,
  VStack,
  Tooltip,
} from "@chakra-ui/react";
import { IField } from "../../../interfaces/IField";
import { Controller } from "react-hook-form";
import { DefinedValidations } from "../../../interfaces/Validations";
import { Asterisk, DrawingIcon } from "../../../icons";
import { InfoOutlineIcon } from "@chakra-ui/icons";
import useValidate from "../../../hooks/useValidate";
import "tui-image-editor/dist/tui-image-editor.css";
import "tui-color-picker/dist/tui-color-picker.css";
import AnnotationModal from "../../AnnotationModal";
import { v4 as uuidv4 } from "uuid";
import axios from "axios";
import { useProjectsContext } from "../../../contexts/ProjectsProvider";
import DrawingRenderer from "./DrawingRenderer";
import { updateFileContent } from "../../../utils/helpers";
import { useAppContext } from "../../../contexts/AppProvider";

interface IFreeHandInput extends IField {
  variant?: string;
  help?: string;
  setValue?: any;
}

const definedValidations: DefinedValidations = {
  notEmpty: (label, validationValue, value) => {
    if (validationValue && !value) {
      return `Cannot be empty`;
    }
  },
};

const FreeHandInput = ({
  control,
  name,
  label,
  tooltip = "",
  validations = {},
  disabled = false,
  formIndex,
}: IFreeHandInput) => {
  const validate = useValidate(
    label || name,
    validations || {},
    definedValidations
  );

  const { currentProject } = useProjectsContext();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedPhoto, setSelectedPhoto] = useState<any>(null);
  const [uploadedImagesBase64, setUploadedImagesBase64] = useState<any>([]);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const { settings } = useAppContext();

  const drawingButtonRef = useRef<null | HTMLButtonElement>(null);

  useEffect(() => {
    setUploadedImagesBase64((control._formValues[name] ?? []).filter(Boolean));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [control._formValues[name], settings]);

  useEffect(() => {
    return () => {
      setUploadedImagesBase64([]);
    };
  }, [formIndex]);

  const uploadFilesForField = async (file: File, fileName: string) => {
    const reportFilesData = new FormData();
    reportFilesData.append(
      "fileLocation",
      `${settings?.spClientFolder}/${(currentProject?.clientName || '').replace(/\//g, ' ')}/${currentProject?.reference}/Photos and Videos/${fileName}`
    );
    reportFilesData.append("spSiteUrl", settings?.spSiteUrl! || '');
    reportFilesData.append("spDocumentLibrary", settings?.spDocumentLibrary! || '');
    reportFilesData.append("file", file);
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/files/saveSignature`,
      reportFilesData,
      { withCredentials: true }
    );
    setIsUploading(false);
    return res.data;
  };

  const renderPhoto = (index, photo, error, onChange) => {
    return isUploading ? (
      <>
        <Flex w="full" h="full" align="center" justify="center">
          <Spinner size="xl" thickness="4px" />
        </Flex>
      </>
    ) : (
      <DrawingRenderer
        name={name}
        key={`drawing-${index}-${formIndex}-${uploadedImagesBase64.length}`}
        photo={photo}
        error={error}
        index={index}
        setSelectedPhoto={setSelectedPhoto}
        selectedPhoto={selectedPhoto}
        onOpen={onOpen}
        disabled={disabled}
        isUploading={isUploading}
        onChange={onChange}
        uploadedImagesBase64={uploadedImagesBase64}
        setUploadedImagesBase64={setUploadedImagesBase64}
        validations={validations}
      />
    );
  };

  const renderContent = (error, onChange) => {
    return (
      <VStack align="left">
        {isUploading ? (
          <>
            <Flex w="full" h="full" align="center" justify="center">
              <Spinner size="xl" thickness="4px" />
            </Flex>
          </>
        ) : (
          uploadedImagesBase64.map((photo, index) =>
            renderPhoto(index, photo, error, onChange)
          )
        )}
      </VStack>
    );
  };
  if (isUploading) {
    return (
      <Flex w='full' h='full' align='center' justify='center' direction={'column-reverse'}>
        <Text fontWeight='bold' fontSize='16px' mt={2}>
          Please wait for uploads to complete
        </Text>
        <Spinner size='xl' thickness='4px' />
      </Flex>
    );
  }

  return (
    <Controller
      name={name}
      control={control}
      rules={{ validate }}
      render={({ field, fieldState }) => {
        const { onChange } = field;
        const { error } = fieldState;

        return (
          <Box w="full" id={name} mt="12px">
            <Text
              color="switch.label.normal"
              fontWeight="bold"
              fontSize="ssm"
              position="static"
              left="none"
              zIndex={1}
            >
              Drawings {!disabled && validations?.notEmpty && <Asterisk h="8px" ml='5px' mb='8px' fill='freeHandInput.iconAsterisk' stroke='freeHandInput.iconAsterisk' />}
              {tooltip && <Tooltip hasArrow label={tooltip} placement='top'><InfoOutlineIcon mb={1} h='14px' /></Tooltip>}
            </Text>
            <Stack align="left">
              {isOpen && <AnnotationModal
                isOpen={isOpen}
                onClose={onClose}
                selectedPhoto={selectedPhoto}
                onClickHandler={async (editedImage) => {
                  onClose()
                  setIsUploading(true);
                  let updatedUploadedImagesBase64;
                  if (selectedPhoto.index === -1) {
                    //save new
                    const fileName: string = uuidv4();
                    let fileId;
                    await uploadFilesForField(editedImage, fileName).then(
                      async (data) => {
                        fileId = await data.fileName.fileId;
                        updatedUploadedImagesBase64 = [
                          ...uploadedImagesBase64,
                          {
                            backupFileName: `${fileName}.png`,
                            index: uploadedImagesBase64.length,
                            fileId,
                            fileName: `${fileName}.png`,
                            title: "",
                            description: "",
                          },
                        ];
                        setUploadedImagesBase64(updatedUploadedImagesBase64);
                      }
                    );
                  } else {
                    //edit existing
                    await updateFileContent(
                      editedImage,
                      `${settings?.spClientFolder}/${(currentProject?.clientName || '').replace(/\//g, ' ')}/${currentProject?.reference}/Photos and Videos/${selectedPhoto.fileName}`,
                      settings
                    ).then(async (data) => {
                      updatedUploadedImagesBase64 = [...uploadedImagesBase64];
                      updatedUploadedImagesBase64[selectedPhoto.index] = {
                        index:
                          updatedUploadedImagesBase64[selectedPhoto.index]
                            .index,
                        fileId:
                          updatedUploadedImagesBase64[selectedPhoto.index]
                            .fileId,
                        fileName:
                          updatedUploadedImagesBase64[selectedPhoto.index]
                            .fileName,
                        title:
                          updatedUploadedImagesBase64[selectedPhoto.index]
                            .title,
                        description:
                          updatedUploadedImagesBase64[selectedPhoto.index]
                            .description,
                        backupFileName:
                          updatedUploadedImagesBase64[selectedPhoto.index]
                            .backupFileName,
                      };
                      setUploadedImagesBase64(updatedUploadedImagesBase64);
                      setSelectedPhoto(null);
                    });
                  }
                  onChange({
                    target: {
                      name,
                      value: updatedUploadedImagesBase64,
                    },
                  });
                  setTimeout(() => {
                    drawingButtonRef.current?.scrollIntoView({ behavior: 'smooth' });
                  }, 200)
                  setIsUploading(false);
                }}
              />}
              {renderContent(error, onChange)}
              <Box>
                {disabled ? uploadedImagesBase64.length === 0 && <Flex>No drawings were added to the form.</Flex> :
                  <Button
                    w="130px"
                    bg="freeHandInput.addDrawingButton.bg"
                    color="freeHandInput.addDrawingButton.color"
                    mt="5px"
                    pl="5px"
                    pr="15px"
                    fontSize="smm"
                    borderRadius="10px"
                    _hover={{
                      bg: "reportFormHeader.buttonDisableBg",
                      color: "reportFormHeader.buttonDisableColor",
                    }}
                    onClick={() => {
                      setSelectedPhoto({
                        imageData:
                          "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wCEAAoHBwgHBgoICAgLCgoLDhgQDg0NDh0VFhEYIx8lJCIfIiEmKzcvJik0KSEiMEExNDk7Pj4+JS5ESUM8SDc9PjsBCgsLDg0OHBAQHDsoIig7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7O//AABEIAtAFAAMBIgACEQEDEQH/xAAVAAEBAAAAAAAAAAAAAAAAAAAAB//EABQQAQAAAAAAAAAAAAAAAAAAAAD/xAAUAQEAAAAAAAAAAAAAAAAAAAAA/8QAFBEBAAAAAAAAAAAAAAAAAAAAAP/aAAwDAQACEQMRAD8AswAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAP/Z",
                        index: -1,
                      });
                      onOpen();
                    }}
                    ref={drawingButtonRef}
                  >
                    <DrawingIcon
                      w="15px"
                      h="15px"
                      mx="10px"
                      stroke="freeHandInput.drawingIcon.stroke"
                    />
                    Add Drawing
                  </Button>
                }
              </Box>
            </Stack>
            {error && (
              <Box
                fontSize="ssm"
                ml={1}
                mt={1}
                color="freeHandInput.label.error"
              >
                {error.message}
              </Box>
            )}
          </Box>
        );
      }}
    />
  );
};

export default FreeHandInput;

export const freeHandInputStyles = {
  freeHandInput: {
    loader: "#4D3069",
    label: {
      normal: "#282F36",
      error: "#E53E3E",
    },
    enableColor: "#282F36",
    disableColor: "#818197",
    color: {
      50: "#ede9ff",
      100: "#c9bff7",
      200: "#a596ea",
      300: "#816ce1",
      400: "#5d42d7",
      500: "#c9bff7",
      600: "#342094",
      700: "#24166b",
      800: "#150d42",
      900: "#07041c",
    },
    tooltipColor: "#9A9EA1",
    deleteIcon: {
      bg: "#F0F0F0",
      stroke: "#818197",
    },
    saveCloseButton: {
      bg: "#edecf7",
      color: "#ffffff",
    },
    addDrawingButton: {
      bg: "#131535",
      color: "#FFFFFF",
    },
    drawingIcon: {
      stroke: "#FFFFFF",
    },
    iconAsterisk: "#DC0043"
  },
};
